import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RightPanel } from './RightPanel';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useRightPanelData } from '@/hooks/useRightPanelData';

export const PersistentNotifications: React.FC = () => {
  const { t } = useTranslation('common');
  const { isNotificationsVisible, showNotificationsBubble, openNotifications, closeNotifications } = useNotifications();
  const {
    data: notificationsData,
    markAllNotificationsRead,
    markNotificationRead,
  } = useRightPanelData();

  return (
    <>
      {/* Notifications Sidebar - positioned on the right with rounded corners */}
      <div 
        className={`
          fixed right-2 bg-white border border-gray-200 shadow-lg z-50 transition-all duration-300 ease-in-out
          rounded-xl overflow-hidden
          ${isNotificationsVisible ? 'w-80 opacity-100' : 'w-0 opacity-0 border-0'}
        `}
        style={{
          top: '48px', // Header height
          height: 'calc(100vh - 48px)', // Full height minus header
        }}
        data-testid="notifications-sidebar"
      >
        {/* Content - Only show when expanded */}
        {isNotificationsVisible && (
          <div className="flex flex-col h-full w-80" data-testid="notifications-content">
            {/* Styled Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                <h3 className="font-semibold text-sm">{t('notifications.title')}</h3>
              </div>
              <button
                onClick={closeNotifications}
                className="p-1 rounded-md hover:bg-white/20 transition-colors"
                aria-label={t('notifications.closeAria')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            
            {/* Notifications Content */}
            <div className="flex-1 overflow-y-auto">
              <RightPanel
                pendingTasks={notificationsData.pendingTasks}
                recentMessages={notificationsData.recentMessages}
                recentActivity={notificationsData.recentActivity}
                notifications={notificationsData.notifications}
                onMarkAllNotificationsRead={markAllNotificationsRead}
                onMarkNotificationRead={markNotificationRead}
              />
            </div>
          </div>
        )}
      </div>

      {/* Collapsed notifications button - separate from sidebar */}
      {showNotificationsBubble && (
        <div className="fixed right-4 bottom-4 z-50" data-testid="notifications-toggle-container">
          <Button
            onClick={openNotifications}
            size="lg"
            title={t('notifications.openTitle')}
            data-testid="notifications-toggle-button"
          >
            <Bell className="h-6 w-6" />
          </Button>
        </div>
      )}
    </>
  );
};
